import { css } from '@emotion/react'
import { useRef } from 'react'
import { Tooltip } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import { AnimatePresence, motion } from 'framer-motion'

import { baseColor, borderColor, disabledIconColor, gray } from '../../../../lib/styles/colors'
import { rippleContainerCss } from '../../../../lib/styles/cssStyles'
import { focusWithinWithBackground } from '../../../../lib/styles/focusIndicator'
import { getFocusColor } from '../../../../lib/colorUtils'
import { ButtonVariant } from '../../buttons/types'
import { Ripple } from '../../buttons'
import useRipple from '../../../../lib/hooks/useRipple'
import { body6Css } from '../../../../lib/styles/typography'

const rootCss = css`
  display: inline-flex;
  width: 58px;
  height: 40px;
  overflow: visible; /* Changed from hidden to allow focus outline to show */
  padding: 12px;
  box-sizing: border-box;
  position: relative;
  flex-shrink: 0;
  z-index: 0;
  vertical-align: middle;
`

const inputCss = css`
  cursor: inherit;
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  margin: 0;
  padding: 0;
  z-index: 1;
`

const containerCss = css`
  svg {
    margin-bottom: -5px;
    margin-left: 5px;
  }
`

const tooltipTextCss = css`
  padding: 6px;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.75px;
`

const baseThumbCss = css`
  box-shadow: 0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14),
    0px 1px 3px 0px rgba(0, 0, 0, 0.12);
  width: 18px;
  height: 18px;
  border-radius: 50%;
`

const uncheckedIconCss = css`
  ${baseThumbCss}
  background-color: #fff;
  border: 1px solid ${disabledIconColor};
`

const checkedIconCss = css`
  ${baseThumbCss}
  background-color: ${baseColor};
  border: 1px solid ${baseColor};
`

const disabledIconCss = css`
  background-color: ${disabledIconColor};
  border: 1px solid ${disabledIconColor};
`

const trackCss = css`
  background-color: ${borderColor};
  border-radius: 7px;
  height: 14px;
  width: 34px;
  z-index: -1;
`

const rippleCss = css`
  ${rippleContainerCss};
  overflow: hidden; /* Contain ripple effect since root is now overflow:visible */
  color: ${baseColor};
  height: 38px;
  width: 38px;
  margin-top: -9px;
  margin-left: -9px;
`

const disabledCss = css`
  cursor: not-allowed;

  :hover {
    cursor: not-allowed;
  }
`

type SwitchProps = {
  /**
   * Optional aria-label for the switch input.
   *
   * **Prefer using only the `label` prop with descriptive text.**
   * Using both can cause WCAG 2.5.3 Label in Name violations.
   */
  ariaLabel?: string
  /** Whether the switch is set to true (on) or false (off) */
  checked?: boolean
  /** Whether the switch is disabled */
  disabled?: boolean
  /** Unique ID to use for the switch */
  id: string
  /** Text to display in the info tooltip */
  infoText?: string
  /** Label to display next to the switch */
  label: string
  /** class name to customize the css of the label */
  labelCssClassName?: string
  /** Callback event when the switch state changes */
  onChange: (e?: React.ChangeEvent<HTMLInputElement>) => void
}

/** Copied and modified from https://github.com/mui-org/material-ui/blob/next/packages/material-ui/src/Switch/Switch.js
 * and https://github.com/mui-org/material-ui/blob/next/packages/material-ui/src/internal/SwitchBase.js
 */
const Switch = ({
  ariaLabel,
  checked = false,
  disabled = false,
  id,
  infoText,
  label,
  labelCssClassName,
  onChange
}: SwitchProps) => {
  const icon = <span css={[uncheckedIconCss, disabled && disabledIconCss]} />
  const checkedIcon = <span css={[checkedIconCss, disabled && disabledIconCss]} />
  const container = useRef<HTMLSpanElement>(null)
  const [ripples, focusRipple, { onTapStart, onTapEnd, onFocus, onBlur }] = useRipple(container)

  const thumbCss = css`
    top: 0;
    left: 0;
    border-radius: 50%;
    display: inline-flex;
    outline: none;
    padding: 9px;
    position: absolute;
    z-index: 1;
    cursor: pointer;
    ${checked && 'transform: translateX(20px)'};
    :hover {
      background-color: ${getFocusColor(baseColor, ButtonVariant.Icon)};
      transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
    }

    ${focusWithinWithBackground(getFocusColor(baseColor, ButtonVariant.Icon), '2px')}
  `

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation()
      event.preventDefault()
      onChange()
    }
  }

  return (
    <div css={containerCss}>
      <span css={rootCss}>
        <motion.span
          css={[thumbCss, disabled && disabledCss]}
          onBlur={onBlur}
          onFocus={onFocus}
          onTap={onTapEnd}
          onTapStart={(e: MouseEvent | TouchEvent | PointerEvent) => {
            onTapStart(e)
          }}
        >
          <input
            aria-label={ariaLabel}
            checked={checked}
            disabled={disabled}
            css={inputCss}
            id={id}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            role="switch"
            type="checkbox"
          />
          <span css={rippleCss} ref={container}>
            <AnimatePresence>
              {ripples.map((r) => (
                <Ripple key={r.key} pulsate={r.pulsate} size={r.size} x={r.x} y={r.y} />
              ))}
              {focusRipple && (
                <Ripple
                  key={focusRipple.key}
                  pulsate={focusRipple.pulsate}
                  size={focusRipple.size}
                  x={focusRipple.x}
                  y={focusRipple.y}
                />
              )}
            </AnimatePresence>
          </span>
          {checked ? checkedIcon : icon}
        </motion.span>
        <span css={trackCss} />
      </span>
      <label className={labelCssClassName} css={body6Css} htmlFor={id}>
        {label}
      </label>
      {infoText && (
        <Tooltip
          tabIndex={0}
          aria-hidden={false}
          aria-label={infoText}
          title={<div css={tooltipTextCss}>{infoText}</div>}
        >
          <InfoIcon fontSize="small" htmlColor={gray} focusable="false" />
        </Tooltip>
      )}
    </div>
  )
}

export default Switch
